import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchPublishedPost } from "../../services/api";
import SEOHead from "../../components/SEO/SEOHead";
import Header from "../../components/Header/Header";
import Rodape from "../../components/Rodape/Rodape";
import { resolveImageUrl } from "../../utils/driveImage";
import { extractHeadings } from "../../utils/markdown";
import MarkdownContent from "../../components/Blog/MarkdownContent";
import TableOfContents from "../../components/Blog/TableOfContents";
import PostCTA from "../../components/Blog/PostCTA";
import PostLikes from "../../components/Blog/PostLikes";
import RelatedPosts from "../../components/Blog/RelatedPosts";
import PostComments from "../../components/Blog/PostComments";
import styles from "./BlogPost.module.css";

// ── Utilitários ───────────────────────────────────────────────────────────────

function formatDate(value) {
    if (!value) return "";
    const normalized = value.endsWith("Z") ? value : `${value.replace("+00:00", "")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(date);
}

function calcReadingTime(content) {
    if (!content) return 1;
    const words = content.trim().split(/\s+/).length;
    return Math.max(1, Math.round(words / 200));
}

// ── Botões de compartilhamento ────────────────────────────────────────────────

function ShareButtons({ title, url }) {
    const [copied, setCopied] = useState(false);

    const waUrl = `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`;
    const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

    function handleCopy() {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }

    return (
        <div className={styles.shareBox}>
            <p className={styles.shareLabel}>Compartilhar</p>
            <div className={styles.shareRow}>
                <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.shareBtn} ${styles.shareBtnWa}`}
                    aria-label="Compartilhar no WhatsApp"
                >
                    {/* WhatsApp icon */}
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    WhatsApp
                </a>
                <a
                    href={liUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.shareBtn} ${styles.shareBtnLi}`}
                    aria-label="Compartilhar no LinkedIn"
                >
                    {/* LinkedIn icon */}
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    LinkedIn
                </a>
                <button
                    type="button"
                    onClick={handleCopy}
                    className={`${styles.shareBtn} ${styles.shareBtnCopy}`}
                    aria-label="Copiar link"
                >
                    {copied ? (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Copiado!
                        </>
                    ) : (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                            </svg>
                            Copiar link
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [readProgress, setReadProgress] = useState(0);

    useEffect(() => {
        setIsLoading(true);
        setPost(null);
        setError("");
        fetchPublishedPost(slug)
            .then((data) => setPost(data.post))
            .catch((err) => setError(err.message || "Post não encontrado."))
            .finally(() => setIsLoading(false));
    }, [slug]);

    // Barra de progresso de leitura
    useEffect(() => {
        function onScroll() {
            const { scrollY } = window;
            const docH = document.documentElement.scrollHeight - window.innerHeight;
            setReadProgress(docH > 0 ? Math.min(100, (scrollY / docH) * 100) : 0);
        }
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const canonical = `${window.location.origin}/blog/${slug}`;
    const coverUrl = post ? resolveImageUrl(post.cover_image) : null;

    const headings = post ? extractHeadings(post.content) : [];

    const readTime = post ? calcReadingTime(post.content) : 1;

    const jsonLd = post
        ? {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: post.title,
              description: post.meta_description || post.excerpt,
              image: coverUrl || undefined,
              datePublished: post.published_at,
              dateModified: post.updated_at,
              author: {
                  "@type": "Person",
                  name: post.author || "HL Software",
              },
              publisher: {
                  "@type": "Organization",
                  name: "HL Software",
                  url: window.location.origin,
              },
              url: canonical,
          }
        : null;

    return (
        <>
            {post && (
                <SEOHead
                    title={`${post.title} — HL Software`}
                    description={post.meta_description || post.excerpt}
                    canonical={canonical}
                    ogImage={coverUrl}
                    ogType="article"
                    jsonLd={jsonLd}
                    articleMeta={{
                        publishedTime: post.published_at,
                        modifiedTime: post.updated_at,
                        author: post.author || "HL Software",
                        section: post.category,
                    }}
                />
            )}

            <Header />

            {/* Barra de progresso fixa no topo */}
            <div
                className={styles.progressBar}
                style={{ width: `${readProgress}%` }}
                aria-hidden="true"
            />

            <main className={styles.page}>
                {/* ── Estado: carregando ── */}
                {isLoading && (
                    <div className={styles.skeletonWrap}>
                        <div className={styles.skeletonTitle} />
                        <div className={styles.skeletonMeta} />
                        <div className={styles.skeletonCover} />
                        <div className={styles.skeletonLine} />
                        <div className={styles.skeletonLine} />
                        <div className={styles.skeletonLineShort} />
                    </div>
                )}

                {/* ── Estado: erro ── */}
                {!isLoading && error && (
                    <div className={styles.errorWrap}>
                        <p className={styles.errorText}>{error}</p>
                        <Link to="/blog" className={styles.backLink}>
                            ← Voltar ao blog
                        </Link>
                    </div>
                )}

                {/* ── Conteúdo do post ── */}
                {post && (
                    <>
                        {/* Breadcrumb */}
                        <div className={styles.breadcrumb}>
                            <Link to="/blog" className={styles.breadcrumbLink}>
                                ← Blog
                            </Link>
                            {post.category && (
                                <>
                                    <span className={styles.breadcrumbSep} aria-hidden="true">·</span>
                                    <span className={styles.breadcrumbCat}>{post.category}</span>
                                </>
                            )}
                        </div>

                        {/* TOC mobile — colapsa acima do artigo */}
                        {headings.length > 0 && (
                            <details className={styles.mobileToc}>
                                <summary className={styles.mobileTocSummary}>
                                    📋 Ver índice do artigo
                                </summary>
                                <div className={styles.mobileTocBody}>
                                    <TableOfContents headings={headings} />
                                </div>
                            </details>
                        )}

                        {/* Layout de duas colunas */}
                        <div className={styles.layout}>
                            {/* ── Coluna principal ── */}
                            <article
                                className={styles.article}
                                itemScope
                                itemType="https://schema.org/BlogPosting"
                            >
                                {/* Header do post */}
                                <div className={styles.articleHeader}>
                                    {post.category && (
                                        <span className={styles.category}>
                                            {post.category}
                                        </span>
                                    )}
                                    <h1 className={styles.title} itemProp="headline">
                                        {post.title}
                                    </h1>
                                    <div className={styles.meta}>
                                        {post.author && (
                                            <span
                                                className={styles.metaItem}
                                                itemProp="author"
                                                itemScope
                                                itemType="https://schema.org/Person"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                    <circle cx="12" cy="7" r="4" />
                                                </svg>
                                                <span itemProp="name">{post.author}</span>
                                            </span>
                                        )}
                                        {post.published_at && (
                                            <time
                                                className={styles.metaItem}
                                                dateTime={post.published_at}
                                                itemProp="datePublished"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                                {formatDate(post.published_at)}
                                            </time>
                                        )}
                                        <span className={styles.metaItem}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <circle cx="12" cy="12" r="10" />
                                                <polyline points="12 6 12 12 16 14" />
                                            </svg>
                                            {readTime} min de leitura
                                        </span>
                                    </div>
                                </div>

                                {/* Capa */}
                                {coverUrl && (
                                    <figure className={styles.cover}>
                                        <img
                                            src={coverUrl}
                                            alt={post.title}
                                            itemProp="image"
                                            loading="eager"
                                        />
                                    </figure>
                                )}

                                {/* Corpo do conteúdo */}
                                <div itemProp="articleBody">
                                    <MarkdownContent content={post.content} />
                                </div>

                                {/* Curtidas */}
                                <PostLikes slug={post.slug} />

                                {/* CTA */}
                                <PostCTA />

                                {/* Posts relacionados */}
                                <RelatedPosts
                                    currentSlug={post.slug}
                                    category={post.category}
                                />

                                {/* Comentários */}
                                <PostComments slug={post.slug} />
                            </article>

                            {/* ── Sidebar sticky ── */}
                            <aside className={styles.sidebar} aria-label="Navegação do artigo">
                                <div className={styles.sidebarSticky}>
                                    <TableOfContents headings={headings} />
                                    <ShareButtons title={post.title} url={canonical} />
                                </div>
                            </aside>
                        </div>
                    </>
                )}
            </main>

            <Rodape />
        </>
    );
}
